import { Component, inject, OnInit } from '@angular/core';
import { OrganizationWithAccesses } from '../../../../core/component/hornavbar/dto/InitDataDTO';
import { EmployeeDTO, UpdateEmployeeManageUsersEvent } from '../../../user/employee/dto/EmployeeDTO';
import { EmployeeRole } from '../../../user/employee/dto/role-type';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { OrganizationDTO } from '../../dto/OrganizationDTO';
import { EmployeeService } from '../../../user/employee/service/employee.service';
import { InitStateBusinessService } from '../../../../core/service/init-state-business.service';
import { AuthService } from '../../../auth/service/auth.service';
import { OrganizationsService } from '../../service/organizations.service';
import { firstValueFrom, Subject } from 'rxjs';
import {
  OrganizationInfoCardComponent,
} from "../../../shared/component/organization-info-card/organization-info-card.component";
import {
  ModalOrganizationUpdateComponent,
} from "../../../shared/component/modal-organization-update/modal-organization-update.component";
import { TeamTableComponent } from "../team-table/team-table.component";
import { ModalDialogComponent } from "../../../shared/component/modal-dialog/modal-dialog.component";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { ModalTeamMemberComponent } from "../team/modal-team-member.component";
import { ShowUIService } from "../../../../core/service/show-ui.service";

@Component({
  selector: 'app-organization-settings.component',
  imports: [
    OrganizationInfoCardComponent,
    ModalOrganizationUpdateComponent,
    TeamTableComponent,
    ModalDialogComponent,
    ModalTeamMemberComponent,
  ],
  templateUrl: './organization-settings.component.html',
  styleUrl: './organization-settings.component.scss',
})
export class OrganizationSettingsComponent implements OnInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  public organizationsService = inject(OrganizationsService);
  private authService = inject(AuthService);
  private initService = inject(InitStateBusinessService);
  private employeeService = inject(EmployeeService);
  private notificationService = inject(NotificationsService);
  private showUIService = inject(ShowUIService);

  isAdmin = false;
  isAbleToManageUsers = false;
  show = new ShowModal();

  selectedOrgWithAccesses: OrganizationWithAccesses;
  isOrgAdmin: boolean;

  employees: EmployeeDTO[];
  userForModal: EmployeeDTO;
  deleteMemberText: string;

  constructor() {
    this.showUIService.setShowSideNavBar(false);
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  async onResendInvite(employee: EmployeeDTO): Promise<void> {
    await firstValueFrom(
      this.employeeService.reInviteUser(employee)
        .pipe(takeUntil(this._destroyStream)),
    );
    this.notificationService.success(NOTIFICATIONS.INVITE_SENT_SUCCESS);
  }

  onDeleteUser(employee: EmployeeDTO): void {
    this.userForModal = employee;
    this.deleteMemberText = `I understand that ${employee.mail} will be deleted`;
    this.show.deleteEmployee = true;
  }

  async onInviteMember(employeeData: EmployeeDTO): Promise<void> {
    try {
      const inviteData = {
        ...employeeData,
        orgUUID: this.selectedOrgWithAccesses.organization.uuid,
      };

      const createdEmployee = await firstValueFrom(
        this.employeeService.create(inviteData)
          .pipe(takeUntil(this._destroyStream)),
      );
      this.employees = [...this.employees, createdEmployee];
      this.notificationService.success(NOTIFICATIONS.INVITE_SENT_SUCCESS);
      this.show.inviteMember = false;
    } catch (error) {
      if (error.status === 422) {
        this.notificationService.error(NOTIFICATIONS.DUPLICATE_EMAIL_ERROR);
      }
    }
  }

  showInviteMemberModal(): void {
    this.show.inviteMember = true;
  }

  async closeModalDeleteUser(isConfirmed: boolean): Promise<void> {
    if (isConfirmed) {
      await firstValueFrom(
        this.employeeService.delete(this.userForModal.uuid)
          .pipe(takeUntil(this._destroyStream)),
      );
      this.employees = this.employees.filter((e) => e.uuid !== this.userForModal.uuid);
    }
    this.show.deleteEmployee = false;
  }

  updatedOrg(org: OrganizationDTO): void {
    this.show.orgUpdate = false;
    if (org) {
      this.initService.refreshInitData();
    }
  }

  async onUpdateIsAbleToManageUsers(data: UpdateEmployeeManageUsersEvent): Promise<void> {
    const updatedEmployee: EmployeeDTO = {
      ...data.employee,
      isAbleToManageUsers: data.isAbleToManageUsers,
    };
    await firstValueFrom(
      this.employeeService.update(updatedEmployee)
        .pipe(takeUntil(this._destroyStream)),
    );

    const employeeIndex = this.employees.findIndex((emp) => emp.uuid === data.employee.uuid);
    if (employeeIndex !== -1) {
      this.employees[employeeIndex] = { ...this.employees[employeeIndex], isAbleToManageUsers: data.isAbleToManageUsers };
    }
  }

  onCancelTeamMemberModal(): void {
    this.show.inviteMember = false;
  }

  private initSubscriptions(): void {
    this.organizationsService.selectedWithAccesses$
      .pipe(
        takeUntil(this._destroyStream),
        filter((org) => Boolean(org)),
        switchMap((org) => {
          this.selectedOrgWithAccesses = org;
          this.isOrgAdmin = this.selectedOrgWithAccesses.userAccesses.isOrgAdmin;
          return this.getAllEmployees();
        }))
      .subscribe(() => {
        this.isAdmin = this.isOrgAdmin || this.selectedOrgWithAccesses.userAccesses.fmsAccess === EmployeeRole.ADMIN;
      });
  }

  private async getAllEmployees(): Promise<void> {
    this.employees = await firstValueFrom(
      this.employeeService.get(this.selectedOrgWithAccesses.organization.uuid)
        .pipe(takeUntil(this._destroyStream)));
    this.checkOrgAccess();
  }

  private checkOrgAccess(): void {
    const userInfo = this.authService.userInfoValue;
    const currentEmployee = this.employees.find((e) => e.mail === userInfo.mail);
    this.isOrgAdmin = currentEmployee.isOrganizationAdmin;
    this.isAdmin = currentEmployee.role === EmployeeRole.ADMIN || this.isOrgAdmin;
    this.isAbleToManageUsers = currentEmployee.isAbleToManageUsers || this.isOrgAdmin;
  }
}


class ShowModal {
  orgUpdate = false;
  deleteEmployee = false;
  inviteMember = false;
}
